import { useState } from 'react';
import { Button } from '@/components/Button';
import { Modal } from '@/features/tickets/Modal';
import { NomeDePessoa, SeletorDePessoa, useDicionarioDePessoas } from '@/features/tickets/pessoas';
import { AvisoDeFalha, CLASSE_DE_CAMPO } from '@/features/workflow/componentes';
import type { Falha } from '@/features/workflow/recusa';
import type { OfertaDeTransicao } from '@/features/workflow/tipos';

/**
 * O que a transição exige, pedido **antes** de enviar.
 *
 * `GET /tickets/:id/transitions` declara `requiredFields` e `requiresReason`
 * junto com cada oferta, e o motor documenta por que eles não barram o botão:
 * vêm de um formulário que ainda não foi aberto. Então o botão acende, o
 * formulário abre, e só o que estiver preenchido é enviado — em vez de deixar
 * a pessoa clicar, receber 422 "preencha antes de continuar" e ter de
 * adivinhar onde preencher.
 *
 * O envio continua podendo ser recusado: um guard pode ter mudado de resposta
 * entre a abertura da tela e o clique. Por isso a recusa é exibida **dentro**
 * do diálogo, com o que foi digitado ainda na tela.
 *
 * ## Campo em branco vale o que já está gravado
 *
 * O servidor **mescla**: `run()` monta os campos como
 * `{ ...camposDaEntidade, ...camposDoCorpo }` para responder ao guard, e é esse
 * objeto mesclado que fica em `TransitionLog.fields` — na trilha da transição,
 * e **não** na ficha. Exigir o preenchimento aqui recusaria uma transição que o
 * servidor aceitaria: o plano de retorno de uma mudança já está na ficha dela,
 * e "enviar para avaliação" não precisa que alguém o digite de novo.
 *
 * Por isso o botão não é travado pelos campos: só a justificativa, que não
 * mora em lugar nenhum antes de existir, continua obrigatória. O que ficar em
 * branco não é enviado, e se também estiver vazio na entidade o servidor
 * recusa — com a lista do que falta, aqui dentro.
 *
 * ## O que se digita aqui não edita a ficha
 *
 * É a outra metade da mesma verdade, e a que engana: escrever o plano de
 * retorno neste diálogo satisfaz o guard **desta** transição e fica registrado
 * como o que foi alegado na hora — a ficha da mudança continua com o texto
 * anterior. Quem quiser mudar o plano muda na aba Mudança. O diálogo diz isso
 * com todas as letras, abaixo dos campos.
 *
 * ## Nem tudo o que a transição exige se digita
 *
 * `changeWindowId` num `<textarea>` é um convite a escrever "sábado à noite"
 * num campo que o motor aceita como preenchido — `preenchido()` só pergunta se
 * há valor —, e a mudança avançaria sem janela nenhuma existir. Identificador
 * tem duas saídas honestas, e o diálogo escolhe conforme o campo: **dizer onde
 * se preenche**, quando existe uma tela própria que valida de verdade (a
 * janela passa por congelamento e colisão em `POST …/schedule`), ou **oferecer
 * o seletor certo**, quando o valor só pode vir daqui — é o caso do gestor do
 * incidente maior, que o efeito `assign.user` lê do próprio corpo.
 */

/**
 * Nome legível para os campos que os fluxos costumam exigir.
 *
 * A chave é a do contexto de expressão do servidor (`TICKET_EXPR_SCHEMA` sem o
 * prefixo). O que não estiver aqui aparece com a própria chave — inventar um
 * rótulo a partir do identificador produziria "Rollback Plan" em português.
 */
const ROTULO_DE_CAMPO: Record<string, string> = {
  resolutionNote: 'Nota de resolução',
  justification: 'Justificativa',
  implementationPlan: 'Plano de implementação',
  testPlan: 'Plano de teste',
  rollbackPlan: 'Plano de retorno',
  // Os dois que os fluxos de mudança da Fase 4 exigem e que só existiam aqui
  // como chave crua: sem rótulo, "enviar para validação" pedia
  // `implementationEvidence` a quem só conhece a ficha em português.
  implementationEvidence: 'Evidência da implementação',
  pirNote: 'Nota da revisão pós-implementação',
  riskLevel: 'Nível de risco',
  changeWindowId: 'Janela de mudança',
  assigneeId: 'Responsável',
  teamId: 'Equipe',
};

/**
 * Os campos que **não** são texto, e onde cada um de fato se preenche.
 *
 * `changeWindowId` num `<textarea>` era um convite a digitar "sábado à noite"
 * num campo que o motor aceita como preenchido — o guard só pergunta se há
 * valor, e a mudança avançaria sem janela nenhuma existir. `assigneeId` e
 * `teamId` têm o mesmo formato: identificador, não frase.
 *
 * A saída não é um seletor aqui dentro. Escolher janela é `POST …/schedule`,
 * que valida congelamento e colisão e move `plannedStart`/`plannedEnd` de tudo
 * o que está pendurado nela; atribuir responsável é o controle do cabeçalho do
 * chamado. Um segundo caminho para as mesmas gravações seria o que ninguém
 * mantém igual. O diálogo diz onde é, e não finge que resolve.
 */
const ONDE_SE_PREENCHE: Record<string, string> = {
  changeWindowId:
    'A janela se escolhe na aba Mudança, em “Janela de mudança” — é lá que o congelamento e a colisão são conferidos antes de a mudança entrar nela.',
  assigneeId: 'O responsável se define no cabeçalho do chamado, em “Responsável”.',
  teamId:
    'A equipe do chamado não é editável em nenhuma tela da Central hoje: ela vem do roteamento. Um fluxo que a exija nesta transição está travado até isso existir — relate.',
  riskLevel:
    'O nível de risco não se digita em lugar nenhum: ele é derivado do questionário, na aba Mudança. O servidor recusa recebê-lo pronto.',
};

/**
 * Os identificadores que **só** podem vir daqui, com o seletor de cada um.
 *
 * `incidentManagerId` não é coluna de chamado nem de mudança: o efeito
 * `assign.user from majorIncident.incidentManagerId` lê o valor do corpo da
 * transição. Mandar a pessoa preencher "na ficha" travaria a mobilização do
 * incidente maior — o que ele precisa é do seletor de pessoa, o mesmo do
 * cabeçalho do chamado.
 */
const ESCOLHA_DE_PESSOA: Record<string, string> = {
  incidentManagerId: 'Quem comanda o incidente maior',
};

/** Um campo terminado em `Id` é identificador, mesmo sem estar nos mapas. */
const ehIdentificador = (campo: string): boolean => /Id$/.test(campo);

export interface DadosDaTransicao {
  fields: Record<string, unknown>;
  reason?: string;
}

export function DialogoDeTransicao({
  oferta,
  aberto,
  ocupado,
  falha,
  aoFechar,
  aoRecarregar,
  aoConfirmar,
}: {
  oferta: OfertaDeTransicao;
  aberto: boolean;
  ocupado: boolean;
  falha: Falha | null;
  aoFechar: () => void;
  /** O que fazer no 409: fechar o diálogo e trazer o estado de agora. */
  aoRecarregar: () => void;
  aoConfirmar: (dados: DadosDaTransicao) => void;
}) {
  const [valores, setValores] = useState<Record<string, string>>({});
  const [motivo, setMotivo] = useState('');

  const identificadores = oferta.requiredFields.filter((campo) => campo in ONDE_SE_PREENCHE);
  const pessoas = oferta.requiredFields.filter((campo) => campo in ESCOLHA_DE_PESSOA);
  /**
   * O que sobra vai para `<textarea>` — menos o que **parece** identificador e
   * não está em nenhum dos dois mapas.
   *
   * A regra do sufixo existe porque os mapas são listas fixas, e um fluxo novo
   * do tenant pode exigir um campo que ninguém previu aqui. Errar oferecendo um
   * campo de identificador com validação é melhor do que errar oferecendo um
   * campo de texto livre: o segundo satisfaz o guard com qualquer coisa.
   */
  const desconhecidos = oferta.requiredFields.filter(
    (campo) =>
      !(campo in ONDE_SE_PREENCHE) && !(campo in ESCOLHA_DE_PESSOA) && ehIdentificador(campo),
  );
  const textuais = oferta.requiredFields.filter(
    (campo) =>
      !(campo in ONDE_SE_PREENCHE) && !(campo in ESCOLHA_DE_PESSOA) && !ehIdentificador(campo),
  );
  const emBranco = textuais.filter((campo) => (valores[campo] ?? '').trim() === '');
  const semMotivo = oferta.requiresReason && motivo.trim() === '';
  // Só a justificativa trava: ela não existe antes de ser escrita. Os demais
  // campos podem já estar gravados na entidade — ver a nota do cabeçalho.
  const incompleto = semMotivo;

  const enviar = () => {
    const fields: Record<string, unknown> = {};
    // Os campos de `ONDE_SE_PREENCHE` ficam de fora: eles não são digitados
    // aqui, e mandá-los à mão faria o guard responder sobre um valor que não
    // corresponde a registro nenhum.
    for (const campo of [...textuais, ...pessoas, ...desconhecidos]) {
      const valor = (valores[campo] ?? '').trim();
      if (valor !== '') fields[campo] = valor;
    }
    aoConfirmar({ fields, ...(motivo.trim() ? { reason: motivo.trim() } : {}) });
  };

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={oferta.name}
      descricao={`O fluxo exige ${descreverExigencias(oferta)} para esta ação.`}
    >
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (!incompleto) enviar();
        }}
      >
        {identificadores.map((campo) => (
          <div
            key={campo}
            className="space-y-1 rounded-md border border-warning/30 bg-warning/10 px-3 py-2.5"
          >
            <p className="text-sm font-medium">
              Esta transição exige <strong>{ROTULO_DE_CAMPO[campo] ?? campo}</strong>, e não é
              aqui que se preenche
            </p>
            <p className="text-sm text-foreground/90">{ONDE_SE_PREENCHE[campo]}</p>
            <p className="font-mono text-xs text-muted-foreground">{campo}</p>
          </div>
        ))}

        {pessoas.map((campo) => (
          <div key={campo} className="space-y-1.5">
            <p className="text-sm font-medium">{ESCOLHA_DE_PESSOA[campo]}</p>
            {valores[campo] ? (
              <p className="text-sm text-muted-foreground">
                Escolhido: <PessoaEscolhida id={valores[campo] as string} />
              </p>
            ) : null}
            <SeletorDePessoa
              rotulo={ESCOLHA_DE_PESSOA[campo] ?? campo}
              desabilitado={ocupado}
              aoEscolher={(pessoa) => setValores((atual) => ({ ...atual, [campo]: pessoa.id }))}
            />
            <p className="font-mono text-xs text-muted-foreground">{campo}</p>
          </div>
        ))}

        {desconhecidos.map((campo) => (
          <div key={campo} className="space-y-1.5">
            <label htmlFor={`campo-${campo}`} className="block text-sm font-medium">
              {ROTULO_DE_CAMPO[campo] ?? campo}
            </label>
            <input
              id={`campo-${campo}`}
              value={valores[campo] ?? ''}
              onChange={(evento) =>
                setValores((atual) => ({ ...atual, [campo]: evento.target.value }))
              }
              className={CLASSE_DE_CAMPO}
              placeholder="identificador (UUID)"
            />
            <p className="text-xs text-muted-foreground">
              Este fluxo exige um <strong>identificador</strong>, e esta tela não sabe de onde ele
              sai — nenhum seletor foi declarado para <code className="font-mono">{campo}</code>.
              Cole o identificador do registro. Texto livre aqui satisfaria o guard e apontaria para
              coisa nenhuma.
            </p>
          </div>
        ))}

        {textuais.map((campo) => (
          <div key={campo} className="space-y-1.5">
            <label htmlFor={`campo-${campo}`} className="block text-sm font-medium">
              {ROTULO_DE_CAMPO[campo] ?? campo}
            </label>
            <textarea
              id={`campo-${campo}`}
              rows={3}
              value={valores[campo] ?? ''}
              onChange={(evento) =>
                setValores((atual) => ({ ...atual, [campo]: evento.target.value }))
              }
              className={CLASSE_DE_CAMPO}
            />
            {/* A chave crua fica à vista: é ela que aparece na trilha da
                transição e nos guards, e quem administra o fluxo precisa
                reconhecê-la. */}
            {ROTULO_DE_CAMPO[campo] ? (
              <p className="font-mono text-xs text-muted-foreground">{campo}</p>
            ) : null}
          </div>
        ))}

        {oferta.requiresReason ? (
          <div className="space-y-1.5">
            <label htmlFor="motivo-da-transicao" className="block text-sm font-medium">
              Justificativa
            </label>
            <textarea
              id="motivo-da-transicao"
              rows={3}
              value={motivo}
              onChange={(evento) => setMotivo(evento.target.value)}
              className={CLASSE_DE_CAMPO}
            />
            <p className="text-xs text-muted-foreground">
              Fica no registro da transição, junto com quem a executou e quando.
            </p>
          </div>
        ) : null}

        {emBranco.length > 0 ? (
          <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            {emBranco.length === 1 ? 'O campo em branco' : `Os ${emBranco.length} campos em branco`}{' '}
            não {emBranco.length === 1 ? 'será enviado' : 'serão enviados'}: vale o que já está
            gravado no chamado.
          </p>
        ) : null}

        {textuais.length > 0 ? (
          <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            O que for escrito aqui vale para <strong>esta</strong> transição e fica no registro
            dela, junto de quem a executou. A ficha do chamado <strong>não</strong> é alterada — o
            plano gravado nela continua o que é. Para mudar o texto da ficha, use o formulário da
            ficha.
          </p>
        ) : null}

        {falha ? <AvisoDeFalha falha={falha} aoRecarregar={aoRecarregar} /> : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
            Cancelar
          </Button>
          <Button type="submit" loading={ocupado} disabled={incompleto}>
            {oferta.name}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** O nome de quem foi escolhido, para a decisão não ficar só num UUID. */
function PessoaEscolhida({ id }: { id: string }) {
  const pessoas = useDicionarioDePessoas([id]);
  return <NomeDePessoa id={id} pessoas={pessoas} />;
}

function descreverExigencias(oferta: OfertaDeTransicao): string {
  const partes: string[] = [];
  if (oferta.requiredFields.length > 0) {
    partes.push(
      oferta.requiredFields.length === 1 ? 'um campo preenchido' : 'alguns campos preenchidos',
    );
  }
  if (oferta.requiresReason) partes.push('uma justificativa');
  return partes.join(' e ');
}
